import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Link as MuiLink,
  Stack,
  Collapse,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  OutlinedInput,
  Badge
} from '@mui/material';
import { Link } from 'react-router-dom';
import NavBar from '../components/NavBar';
import { useSidebar } from '../contexts/SidebarContext';
import { formatDistanceToNow } from 'date-fns';
import axios from 'axios';
import { supabase } from '../supabaseClient';
import { API_URL } from '../globals';
import { 
  CheckCircle, 
  XCircle,
  ExternalLink,
  Video,
  FileSpreadsheet,
  Eye,
  Clock,
  Timer,
  Layers,
  ChevronDown,
  ChevronUp,
  Filter,
  X,
  SkipForward,
  AlertTriangle,
  AlertCircle
} from 'lucide-react';

const PlaywrightTestGroup = () => {
  const [testResults, setTestResults] = useState([]);
  const [groupMetadata, setGroupMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // URL search parameters
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize filter states from URL parameters
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const statusParam = searchParams.get('status');
    return statusParam ? statusParam.split(',') : [];
  });
  const [selectedProjects, setSelectedProjects] = useState(() => {
    const projectParam = searchParams.get('project');
    return projectParam ? projectParam.split(',') : [];
  });
  const [selectedCategories, setSelectedCategories] = useState(() => {
    const categoryParam = searchParams.get('category');
    return categoryParam ? categoryParam.split(',') : [];
  });
  
  // Unified expansion state - only one can be expanded at a time
  const [expandedSection, setExpandedSection] = useState(null); // 'filters' | 'metadata' | null
  
  const { isOpen, isHovered } = useSidebar();
  const navbarWidth = isOpen || isHovered ? 240 : 64;
  const params = useParams();
  const groupId = params.groupId;

  // Extract unique values for filters
  const { uniqueStatuses, uniqueProjects, uniqueCategories, filteredResults } = useMemo(() => {
    const statuses = [...new Set(testResults.map(result => result.result_dict.status))];
    const projects = [...new Set(testResults.map(result => result.result_dict.projectName))];
    const categories = [...new Set(testResults
      .map(result => result.result_dict.category)
      .filter(category => category !== undefined && category !== null)
    )];
    
    // Define status priority for sorting
    const statusPriority = {
      'failed': 0,
      'passed': 1,
      'skipped': 2
    };

    // Apply filters and sort
    const filtered = testResults
      .filter(result => {
        const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(result.result_dict.status);
        const projectMatch = selectedProjects.length === 0 || selectedProjects.includes(result.result_dict.projectName);
        const categoryMatch = selectedCategories.length === 0 || selectedCategories.includes(result.result_dict.category);
        return statusMatch && projectMatch && categoryMatch;
      })
      .sort((a, b) => {
        // Sort by status priority
        const statusA = statusPriority[a.result_dict.status] ?? 999; // Unknown statuses go to the end
        const statusB = statusPriority[b.result_dict.status] ?? 999;
        return statusA - statusB;
      });

    return {
      uniqueStatuses: statuses,
      uniqueProjects: projects,
      uniqueCategories: categories,
      filteredResults: filtered
    };
  }, [testResults, selectedStatuses, selectedProjects, selectedCategories]);

  // Sync filter state with URL parameters on URL change
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const projectParam = searchParams.get('project');
    const categoryParam = searchParams.get('category');
    
    setSelectedStatuses(statusParam ? statusParam.split(',') : []);
    setSelectedProjects(projectParam ? projectParam.split(',') : []);
    setSelectedCategories(categoryParam ? categoryParam.split(',') : []);
  }, [searchParams]);

  useEffect(() => {
    const fetchTestGroupData = async () => {
      try {
        let authToken = null;
        try {
          const sessionRes = await supabase.auth.getSession();
          authToken = sessionRes?.data?.session?.access_token || null;
        } catch (_) {
          authToken = null;
        }
        const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

        // Fetch test results
        const resultsResponse = await axios.get(`${API_URL}/test/group_results?group_id=${groupId}`, {
          headers: headers,
        });

        if (resultsResponse.data) {
          setTestResults(resultsResponse.data);
        }

        // Fetch group details (including metadata)
        try {
          const groupDetailsResponse = await axios.get(`${API_URL}/test/group_details?group_id=${groupId}`, {
            headers: headers,
          });
          
          if (groupDetailsResponse.data) {
            setGroupMetadata(groupDetailsResponse.data);
          }
        } catch (metadataError) {
          // Group details fetch failed, but don't fail the whole component
          console.warn('Could not fetch group details:', metadataError);
        }

      } catch (err) {
        console.error('Error fetching test group data:', err);
        setError(err.message || 'Failed to fetch test group data');
      } finally {
        setLoading(false);
      }
    };

    if (groupId) {
      fetchTestGroupData();
    }
  }, [groupId]);

  // Helper function to format the date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'Unknown time';
    }
  };

  // Helper function to render metadata value
  const renderMetadataValue = (value) => {
    const isUrl = typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'));
    
    if (isUrl) {
      return (
        <MuiLink
          href={value}
          target="_blank" 
          rel="noopener noreferrer"
          sx={{ 
            color: '#64b5f6',
            textDecoration: 'none',
            fontSize: '0.875rem',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          {value}
        </MuiLink>
      );
    }
    
    return (
      <Typography sx={{ color: '#e0e0e0', fontSize: '0.875rem' }}>
        {value}
      </Typography>
    );
  };

  // Unified Tab Interface Component
  const ControlTabs = () => {
    const hasActiveFilters = selectedStatuses.length > 0 || selectedProjects.length > 0 || selectedCategories.length > 0;
    const totalFilters = selectedStatuses.length + selectedProjects.length + selectedCategories.length;
    const hasMetadata = groupMetadata?.metadata && Object.keys(groupMetadata.metadata).length > 0;
    
    if (!hasMetadata && uniqueStatuses.length === 0 && uniqueProjects.length === 0 && uniqueCategories.length === 0) {
      return null;
    }

    const handleTabClick = (tabName) => {
      setExpandedSection(expandedSection === tabName ? null : tabName);
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#1A1A1A',
            borderRadius: 1,
            border: '1px solid rgba(255, 255, 255, 0.08)',
            overflow: 'hidden',
            width: '100%'
          }}
        >
          {/* Tab Bar */}
          <Box sx={{ 
            display: 'flex',
            borderBottom: expandedSection ? '1px solid rgba(255, 255, 255, 0.08)' : 'none'
          }}>
            {/* Filters Tab */}
            {(uniqueStatuses.length > 0 || uniqueProjects.length > 0 || uniqueCategories.length > 0) && (
              <Box
                onClick={() => handleTabClick('filters')}
                sx={{
                  px: 2,
                  py: 2,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  backgroundColor: expandedSection === 'filters' ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                  borderBottom: expandedSection === 'filters' ? '2px solid rgba(255, 255, 255, 0.3)' : '2px solid transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.02)'
                  }
                }}
              >
                <Filter size={16} style={{ color: '#888' }} />
                <Typography sx={{ 
                  color: '#fff', 
                  fontWeight: 500, 
                  fontSize: '0.9rem'
                }}>
                  Filters
                </Typography>
                
                {hasActiveFilters && (
                  <Badge
                    badgeContent={totalFilters}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#666',
                        color: '#fff',
                        fontSize: '0.7rem',
                        minWidth: '18px',
                        height: '18px'
                      }
                    }}
                  >
                    <Box />
                  </Badge>
                )}

                {hasActiveFilters && expandedSection === 'filters' && (
                  <Chip
                    label="Clear All"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearAllFilters();
                    }}
                    sx={{
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      color: '#f44336',
                      border: '1px solid rgba(244, 67, 54, 0.3)',
                      height: '24px',
                      fontSize: '0.7rem',
                      ml: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.2)'
                      }
                    }}
                    deleteIcon={<X size={12} style={{ color: '#f44336' }} />}
                    onDelete={(e) => {
                      e.stopPropagation();
                      clearAllFilters();
                    }}
                  />
                )}
              </Box>
            )}

            {/* Metadata Tab */}
            {hasMetadata && (
              <Box
                onClick={() => handleTabClick('metadata')}
                sx={{
                  px: 2,
                  py: 2,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  backgroundColor: expandedSection === 'metadata' ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                  borderBottom: expandedSection === 'metadata' ? '2px solid rgba(255, 255, 255, 0.3)' : '2px solid transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.02)'
                  }
                }}
              >
                <Layers size={16} style={{ color: '#888' }} />
                <Typography sx={{ 
                  color: '#fff', 
                  fontWeight: 500, 
                  fontSize: '0.9rem'
                }}>
                  Metadata ({Object.keys(groupMetadata.metadata).length})
                </Typography>
              </Box>
            )}
          </Box>

          {/* Metadata Content */}
          <Collapse in={expandedSection === 'metadata'}>
            <Box sx={{ px: 3, pb: 3, pt: 2 }}>
              <Box sx={{ 
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'flex-start'
              }}>
                {hasMetadata && Object.entries(groupMetadata.metadata).map(([key, value]) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      minWidth: 'fit-content'
                    }}
                  >
                    <Typography sx={{ 
                      color: '#999', 
                      fontSize: '0.8rem',
                      fontWeight: 400,
                      flexShrink: 0
                    }}>
                      {key}:
                    </Typography>
                    
                    <Box>
                      {renderMetadataValue(value)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Collapse>

          {/* Filters Content */}
          <Collapse in={expandedSection === 'filters'}>
            <Box sx={{ px: 3, pb: 3, pt: 2, width: '100%', minWidth: 0 }}>
              {/* Status Filters */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ 
                  color: '#ccc', 
                  fontSize: '0.8rem', 
                  fontWeight: 500, 
                  mb: 1.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Status
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {uniqueStatuses.map((status) => {
                    const isSelected = selectedStatuses.includes(status);
                    const count = testResults.filter(r => r.result_dict.status === status).length;
                    
                    return (
                      <Chip
                        key={status}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                            <span style={{ textTransform: 'capitalize' }}>{status}</span>
                            <Badge
                              badgeContent={count}
                              sx={{
                                ml: 0.5,
                                mr: 0.5,
                                '& .MuiBadge-badge': {
                                  backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                                  color: '#fff',
                                  fontSize: '0.6rem',
                                  minWidth: '16px',
                                  height: '16px',
                                }
                              }}
                            >
                              <Box />
                            </Badge>
                          </Box>
                        }
                        onClick={() => toggleStatusFilter(status)}
                        sx={{
                          backgroundColor: isSelected 
                            ? 'rgba(33, 150, 243, 0.2)'
                            : 'rgba(255, 255, 255, 0.05)',
                          color: isSelected 
                            ? '#2196f3'
                            : '#ccc',
                          border: `1px solid ${isSelected 
                            ? '#2196f3' 
                            : 'rgba(255, 255, 255, 0.1)'}`,
                          '&:hover': {
                            backgroundColor: isSelected 
                              ? 'rgba(33, 150, 243, 0.3)'
                              : 'rgba(255, 255, 255, 0.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>

              {/* Project Filters */}
              {uniqueProjects.length > 0 && (
                <Box sx={{ mb: uniqueCategories.length > 0 ? 3 : 0 }}>
                  <Typography sx={{ 
                    color: '#ccc', 
                    fontSize: '0.8rem', 
                    fontWeight: 500, 
                    mb: 1.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Projects
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {uniqueProjects.map((project) => {
                      const isSelected = selectedProjects.includes(project);
                      const count = testResults.filter(r => r.result_dict.projectName === project).length;
                      
                      return (
                        <Chip
                          key={project}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{project}</span>
                              <Badge
                                badgeContent={count}
                                sx={{
                                  ml: 0.5,
                                  mr: 0.5,
                                  '& .MuiBadge-badge': {
                                    backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                                    color: '#fff',
                                    fontSize: '0.6rem',
                                    minWidth: '16px',
                                    height: '16px'
                                  }
                                }}
                              >
                                <Box />
                              </Badge>
                            </Box>
                          }
                          onClick={() => toggleProjectFilter(project)}
                          sx={{
                            backgroundColor: isSelected 
                              ? 'rgba(33, 150, 243, 0.2)'
                              : 'rgba(255, 255, 255, 0.05)',
                            color: isSelected 
                              ? '#2196f3'
                              : '#ccc',
                            border: `1px solid ${isSelected 
                              ? '#2196f3' 
                              : 'rgba(255, 255, 255, 0.1)'}`,
                            '&:hover': {
                              backgroundColor: isSelected 
                                ? 'rgba(33, 150, 243, 0.3)'
                                : 'rgba(255, 255, 255, 0.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* Category Filters */}
              {uniqueCategories.length > 0 && (
                <Box>
                  <Typography sx={{ 
                    color: '#ccc', 
                    fontSize: '0.8rem', 
                    fontWeight: 500, 
                    mb: 1.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Categories
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {uniqueCategories.map((category) => {
                      const isSelected = selectedCategories.includes(category);
                      const count = testResults.filter(r => r.result_dict.category === category).length;
                      
                      return (
                        <Chip
                          key={category}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{category}</span>
                              <Badge
                                badgeContent={count}
                                sx={{
                                  ml: 0.5,
                                  mr: 0.5,
                                  '& .MuiBadge-badge': {
                                    backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                                    color: '#fff',
                                    fontSize: '0.6rem',
                                    minWidth: '16px',
                                    height: '16px'
                                  }
                                }}
                              >
                                <Box />
                              </Badge>
                            </Box>
                          }
                          onClick={() => toggleCategoryFilter(category)}
                          sx={{
                            backgroundColor: isSelected 
                              ? 'rgba(33, 150, 243, 0.2)'
                              : 'rgba(255, 255, 255, 0.05)',
                            color: isSelected 
                              ? '#2196f3'
                              : '#ccc',
                            border: `1px solid ${isSelected 
                              ? '#2196f3' 
                              : 'rgba(255, 255, 255, 0.1)'}`,
                            '&:hover': {
                              backgroundColor: isSelected 
                                ? 'rgba(33, 150, 243, 0.3)'
                                : 'rgba(255, 255, 255, 0.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Box>
          </Collapse>
        </Paper>
      </Box>
    );
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'passed':
        return '#4caf50';
      case 'skipped':
        return '#ff9800';
      default:
        return '#f44336';
    }
  };

  // Helper function to update URL with current filter state
  const updateURL = (newStatuses, newProjects, newCategories) => {
    const params = new URLSearchParams();
    
    if (newStatuses.length > 0) {
      params.set('status', newStatuses.join(','));
    }
    if (newProjects.length > 0) {
      params.set('project', newProjects.join(','));
    }
    if (newCategories.length > 0) {
      params.set('category', newCategories.join(','));
    }
    
    setSearchParams(params);
  };

  // Helper function to clear all filters
  const clearAllFilters = () => {
    setSelectedStatuses([]);
    setSelectedProjects([]);
    setSelectedCategories([]);
    updateURL([], [], []);
  };

  // Helper function to toggle filter selection
  const toggleStatusFilter = (status) => {
    const newStatuses = selectedStatuses.includes(status) 
      ? selectedStatuses.filter(s => s !== status)
      : [...selectedStatuses, status];
    
    setSelectedStatuses(newStatuses);
    updateURL(newStatuses, selectedProjects, selectedCategories);
  };

  const toggleProjectFilter = (project) => {
    const newProjects = selectedProjects.includes(project) 
      ? selectedProjects.filter(p => p !== project)
      : [...selectedProjects, project];
    
    setSelectedProjects(newProjects);
    updateURL(selectedStatuses, newProjects, selectedCategories);
  };

  const toggleCategoryFilter = (category) => {
    const newCategories = selectedCategories.includes(category) 
      ? selectedCategories.filter(c => c !== category)
      : [...selectedCategories, category];
    
    setSelectedCategories(newCategories);
    updateURL(selectedStatuses, selectedProjects, newCategories);
  };



  if (loading) {
    return (
      <>
        <NavBar />
        <Box
          component="main" 
          sx={{
            minHeight: '100vh',
            marginLeft: `${navbarWidth}px`,
            transition: 'margin-left 0.2s',
            width: `calc(100% - ${navbarWidth}px)`,
            p: 3,
            backgroundColor: '#121212'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
            <CircularProgress />
          </Box>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <NavBar />
        <Box
          component="main" 
          sx={{
            minHeight: '100vh',
            marginLeft: `${navbarWidth}px`,
            transition: 'margin-left 0.2s',
            width: `calc(100% - ${navbarWidth}px)`,
            p: 3,
            backgroundColor: '#121212'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh', flexDirection: 'column' }}>
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Typography variant="body2" sx={{ color: '#888' }}>
              Please check the group ID or try again later.
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <NavBar />
      <Box
        component="main" 
        sx={{
          minHeight: '100vh',
          marginLeft: `${navbarWidth}px`,
          transition: 'margin-left 0.2s',
          width: `calc(100% - ${navbarWidth}px)`,
          p: 3,
          backgroundColor: '#121212',
          boxSizing: 'border-box'
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
            Test Group Results
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Typography sx={{ color: '#888' }}>
              Group ID: {groupId}
            </Typography>
            {(selectedStatuses.length > 0 || selectedProjects.length > 0 || selectedCategories.length > 0) && (
              <Typography sx={{ color: '#888', fontSize: '0.875rem', mx: 1 }}>
                •
              </Typography>
            )}
            <Typography sx={{ color: '#888', fontSize: '0.875rem', ml: 2 }}>
              Showing {filteredResults.length} of {testResults.length} results
            </Typography>
          </Box>
        </Box>

        <ControlTabs />

        {filteredResults.length === 0 && testResults.length > 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            color: '#888'
          }}>
            <Filter size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <Typography variant="h6" sx={{ mb: 1, color: '#ccc' }}>
              No results match your filters
            </Typography>
            <Typography sx={{ fontSize: '0.875rem' }}>
              Try adjusting or clearing your filter selection
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filteredResults.map((result) => (
            <Paper
              key={result.run_id}
              elevation={0}
              sx={{
                backgroundColor: '#1A1A1A',
                borderRadius: 1,
                border: '1px solid rgba(255, 255, 255, 0.05)',
                overflow: 'hidden',
                '&:hover': {
                  backgroundColor: '#242424'
                }
              }}
            >
              <Box 
                sx={{ 
                  px: 2, 
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                {/* Status Icon */}
                {result.result_dict.status === 'passed' ? (
                  <CheckCircle size={16} style={{ color: '#4caf50', flexShrink: 0 }} />
                ) : result.result_dict.status === 'skipped' ? (
                  <SkipForward size={16} style={{ color: '#ff9800', flexShrink: 0 }} />
                ) : (
                  <XCircle size={16} style={{ color: '#f44336', flexShrink: 0 }} />
                )}

                {/* Title with Reason Icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                  <Typography 
                    component="a"
                    href={`/test/run/${result.run_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      color: '#fff',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {result.result_dict.title}
                  </Typography>

                  {/* Reason (if exists) */}
                  {result.result_dict.reason && (
                    <Tooltip title={result.result_dict.reason} arrow>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {result.result_dict.status === 'passed' ? (
                          <AlertTriangle size={16} style={{ color: '#ffc107' }} />
                        ) : (
                          <AlertCircle size={16} style={{ color: '#f44336' }} />
                        )}
                      </Box>
                    </Tooltip>
                  )}
                </Box>

                {/* Project */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#888', flexShrink: 0 }}>
                  <Layers size={14} />
                  <Typography sx={{ fontSize: '0.75rem' }}>
                    {result.result_dict.projectName}
                  </Typography>
                </Box>

                {/* Duration */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#888', flexShrink: 0 }}>
                  <Timer size={14} />
                  <Typography sx={{ fontSize: '0.75rem' }}>
                    {(result.result_dict.duration / 1000).toFixed(1)}s
                  </Typography>
                </Box>

                {/* Time */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#888', flexShrink: 0, minWidth: '120px' }}>
                  <Clock size={14} />
                  <Typography 
                    component="a"
                    href={`/test/run/${result.run_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      fontSize: '0.75rem',
                      color: '#888',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#ccc'
                      }
                    }}
                  >
                    {formatDate(result.created_at)}
                  </Typography>
                </Box>

                {/* Artifacts */}
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  {result.result_dict.artifacts?.screenshots?.length > 0 && (
                    <Tooltip title="View Screenshots">
                      <IconButton 
                        component={Link}
                        to={`/test/run/${result.run_id}`}
                        size="small"
                        sx={{ color: '#666' }}
                      >
                        <Eye size={14} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {result.result_dict.artifacts?.video?.url && (
                    <Tooltip title="View Recording">
                      <IconButton 
                        component={Link}
                        to={`/test/run/${result.run_id}`}
                        size="small"
                        sx={{ color: '#666' }}
                      >
                        <Video size={14} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {result.result_dict.artifacts?.trace?.url && (
                    <Tooltip title="View Trace">
                      <IconButton
                        component={MuiLink}
                        href={`https://trace.playwright.dev/index.html?trace=${encodeURIComponent(result.result_dict.artifacts.trace.url)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        sx={{ color: '#666' }}
                      >
                        <FileSpreadsheet size={14} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
            </Paper>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default PlaywrightTestGroup; 